import { memo, MouseEvent, useCallback, useEffect, useMemo } from 'react';

import {
  DeleteFilled,
  EditFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { message, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { History } from 'history';
import _ from 'lodash';
import { Link, useLocation, withRouter } from 'react-router-dom';
import { useTableUrlSync } from '../../../../../hooks/useTableUrlSync';
import './UserTableList.less';

import { ApiError, handleError } from '../../../../../api/base';
import { useAuth } from '../../../../../hooks/useAuth';
import { UserModel } from '../../../../../models/user';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { fetchDropdownClients } from '../../../../../store/features/clientDropdown/clientDropdownSlice';
import { deleteUser } from '../../../../../store/features/users/usersSlice';
import {
  formatDate,
  propsAreEqual,
  UserPermissions,
} from '../../../../../util';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';

const { Text } = Typography;

interface UserTableListProps {
  users: UserModel[];
  loading: boolean;
  history: History;
}

const UserTableList = ({ users, loading, history }: UserTableListProps) => {
  const { hasPermission } = useAuth();

  const location = useLocation();
  const currentPath = useMemo(() => location.pathname, [location]);
  const dispatch = useAppDispatch();
  const { clients } = useAppSelector((state) => state.clientDropdown);
  const {
    totalCount: usersTotalCount,
    page,
    pageSize,
  } = useAppSelector((state) => state.users);
  const hasClientPermission = useMemo((): boolean => {
    return hasPermission([
      UserPermissions.ClientsView,
      UserPermissions.ClientsEdit,
      UserPermissions.ClientsDelete,
    ]);
  }, [hasPermission]);

  const onError = useCallback((err: ApiError) => {
    message.destroy('deleting-user-message');
    handleError(err);
  }, []);

  const handleDeleteUser = useCallback(
    async (user: UserModel) => {
      message.loading(
        { content: 'Deleting user...', key: 'deleting-user-message' },
        0
      );

      const res: any = await dispatch(deleteUser(user.uuid));
      if (res.error) {
        onError({ error: res.payload.error });
      } else {
        message.destroy('deleting-user-message');
        message.success(`User '${user.name}' deleted.`);
      }
    },
    [onError, dispatch]
  );

  const handleConfirmDeleteUser = useCallback(
    (e: MouseEvent, user: UserModel) => {
      e.preventDefault();
      Modal.confirm({
        title: `Delete user '${user.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover this user.',
        okText: 'Delete',
        onOk: () => handleDeleteUser(user),
      });
    },
    [handleDeleteUser]
  );

  const getClientName = useCallback(
    (clientId: string): string => {
      const client = clients.find((c) => c.uuid === clientId);
      return client?.name || '';
    },
    [clients]
  );

  const SORT_FIELD_MAP: Record<string, string> = useMemo(
    () => ({
      updated_at: 'updated_at',
    }),
    []
  );

  const actionsColumn: any = useMemo(
    () => ({
      title: 'Actions',
      key: 'actions',
      width: 180,
      dataIndex: 'uuid',
      align: 'right',
      render: (_: string, user: UserModel) => {
        return (
          <Space direction="horizontal" size="middle">
            {hasPermission(UserPermissions.UsersEdit) && (
              <Tooltip title="Edit">
                <EditFilled
                  onClick={() =>
                    history.push({
                      hash: DrawerHashRoute.UserForm,
                      state: { data: user },
                    })
                  }
                  className="users-table-list-action-icon"
                />
              </Tooltip>
            )}
            {hasPermission(UserPermissions.UsersDelete) && (
              <Tooltip title="Delete">
                <DeleteFilled
                  onClick={(e) => handleConfirmDeleteUser(e, user)}
                  className="users-table-list-action-icon"
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    }),
    [hasPermission, handleConfirmDeleteUser, history]
  );

  // Centralized table URL sync
  const { currentSortField, currentSortOrder, onTableChange } = useTableUrlSync(
    {
      location,
      history,
      page,
      pageSize,
      sortFieldMap: SORT_FIELD_MAP,
    }
  );

  const columns: any = useMemo(() => {
    const cols = [
      {
        title: 'No.',
        dataIndex: 'index',
        key: 'index',
        width: 50,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Name',
        width: 100,
        render: (user: UserModel) => (
          <Link className="ant-btn-link" to={`/users/${user.uuid}`}>
            {user.name}
          </Link>
        ),
      },
      {
        title: 'Client',
        dataIndex: 'client_id',
        key: 'client_id',
        width: 200,
        render: (client: string) => {
          if (!!client) {
            const clientName = getClientName(client);
            return (
              <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
                {clientName}
              </div>
            );
          }
          return <Text type="secondary">N/A</Text>;
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 250,
        render: (email: string) =>
          !!email ? (
            <Tooltip title={email} mouseEnterDelay={1}>
              <Text ellipsis style={{ maxWidth: '400px' }}>
                {email}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              No email.
            </Text>
          ),
      },
      {
        title: 'Roles',
        dataIndex: 'roles',
        key: 'roles',
        width: 200,
        render: (roles: { key: string }[]) => {
          return _.uniqBy(roles, 'display_name').map(
            (p: { display_name: string }) => {
              if (!!p.display_name) {
                return (
                  <Tag
                    color="#EBF9FC"
                    style={{ color: '#003D4C', borderColor: '#003D4C' }}
                  >
                    {p.display_name}
                  </Tag>
                );
              }
              return null;
            }
          );
        },
      },
      {
        title: 'Last updated',
        dataIndex: 'updated_at',
        display_name: 'updated_at',
        width: 200,
        render: (date: string) => {
          return (
            <div>
              {date ? formatDate(date, undefined, 'America/Chicago') : null}
            </div>
          );
        },
        sorter: true,
        sortOrder: currentSortField === 'updated_at' ? currentSortOrder : null,
      },
    ];

    if (!currentPath.includes('/clients/')) {
      cols.push(actionsColumn);
    }

    return cols;
  }, [
    actionsColumn,
    currentPath,
    getClientName,
    currentSortField,
    currentSortOrder,
  ]);

  useEffect(() => {
    if (hasClientPermission) {
      dispatch(fetchDropdownClients({}));
    }
  }, [dispatch, hasClientPermission]);

  return (
    <Table
      loading={loading}
      locale={{ emptyText: <></> }}
      rowKey={(user) => `${user.uuid}-row-key`}
      sortDirections={['ascend', 'descend', 'ascend']}
      className="users-table-list"
      onChange={onTableChange}
      pagination={{
        current: page,
        pageSize: pageSize,
        total: usersTotalCount,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      columns={columns}
      dataSource={users}
      scroll={{ x: 1000 }}
    />
  );
};

export default withRouter<any, any>(memo(UserTableList, propsAreEqual));
